import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Megaphone, Eye, MapPin, Target, Shield, TrendingUp, MessageCircle } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import ContactForm from '@/components/ContactForm';

const BIKE_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/48y6aysc_image.png";

const stats = [
  { label: 'Daily km covered', icon: MapPin },
  { label: 'Daily impressions', icon: Eye },
  { label: 'Urban coverage', icon: Target },
  { label: 'Visibility', icon: TrendingUp },
];

const benefits = [
  {
    icon: Eye,
    title: 'Maximum Visibility',
    description: 'Your brand travels through high-traffic urban areas all day, every day.',
  },
  {
    icon: MapPin,
    title: 'Targeted Reach',
    description: 'Reach consumers in specific areas — from CBDs to residential neighborhoods.',
  },
  {
    icon: Shield,
    title: 'Purpose-Driven',
    description: 'Your advertising spend supports rider safety and empowerment programs.',
  },
  {
    icon: TrendingUp,
    title: 'Measurable Impact',
    description: 'Track your campaign reach with detailed analytics and reporting.',
  },
];

const adOptions = [
  {
    title: 'Delivery Box Branding',
    description: '#MyLifeMatters and the Corporate Logo on the back of the box.',
    coverage: 'Maximum',
  },
];

const AdvertisingPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="advertising-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 via-slate-50 to-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold tracking-wide uppercase">
                  Advertising With Purpose
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900">
                  Your Brand, <span className="text-primary-mlm">Moving Through Cities</span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  Turn delivery bikes into moving billboards. Reach millions of consumers while 
                  supporting rider safety and empowerment.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    asChild
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold shadow-lg"
                    data-testid="advertise-btn"
                  >
                    <a href="#enquiry-form">
                      Start Advertising
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 font-semibold"
                  >
                    <a href="https://wa.me/27720286584?text=Hi%2C%20I%27m%20interested%20in%20advertising%20on%20delivery%20bikes" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 w-5 h-5" />
                      Get Quote
                    </a>
                  </Button>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <img
                  src={BIKE_IMAGE}
                  alt="Branded Delivery Bike"
                  className="rounded-3xl shadow-xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-blue-500 rounded-2xl p-6 shadow-xl text-white">
                  <Megaphone className="w-8 h-8 mb-2" />
                  <p className="text-lg font-semibold">Moving Billboards</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-900">
        <div className="container-wide">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <FadeIn key={stat.label} delay={0.1 * index}>
                <div className="text-center">
                  <stat.icon className="w-10 h-10 text-safety-yellow mx-auto mb-4" />
                  <p className="text-slate-400">{stat.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Why Bike Advertising?
              </h2>
              <p className="text-lg text-slate-600">
                Traditional billboards are static. Delivery bikes move through communities, 
                reaching consumers where they live, work, and play.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <FadeIn key={benefit.title} delay={0.1 * index}>
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Advertising Options */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Branded Last Mile Solutions
              </h2>
              <p className="text-lg text-slate-600">
                Take Your Brand to the Streets
              </p>
            </div>
          </FadeIn>

          <div className="flex justify-center">
            {adOptions.map((option, index) => (
              <FadeIn key={option.title} delay={0.1 * index}>
                <div className="bg-white rounded-2xl p-8 border-2 border-primary-mlm shadow-xl max-w-md">
                  <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">{option.title}</h3>
                  <p className="text-slate-600 mb-4">{option.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Coverage:</span>
                    <span className="text-sm font-semibold text-emerald-600">{option.coverage}</span>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Social Impact */}
      <section className="py-20 md:py-32 bg-slate-900">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
                Advertising That Makes a Difference
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                When you advertise with #MyLifeMatters, your investment doesn't just buy visibility — 
                it funds rider safety training, equipment, and support programs. Every impression 
                is an investment in someone's future.
              </p>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                {[
                  { value: 'Safety Training', label: 'Funded' },
                  { value: 'Rider Support', label: 'Enabled' },
                  { value: 'Communities', label: 'Impacted' },
                ].map((item) => (
                  <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <p className="text-2xl font-heading font-bold text-safety-yellow mb-1">{item.value}</p>
                    <p className="text-white">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" className="py-20 md:py-32 bg-white scroll-mt-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold tracking-wide uppercase">
                  Get Started
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900">
                  Request a Quote
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Tell us about your campaign goals and we'll create a custom advertising 
                  package that delivers results.
                </p>
                <img
                  src={BIKE_IMAGE}
                  alt="Branded Delivery Bike"
                  className="rounded-2xl shadow-lg w-full max-w-md"
                />
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="bg-slate-50 rounded-2xl p-8">
                <ContactForm formType="advertising" showCompany={true} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvertisingPage;
